<template>
	<div class="container">
		<order-progress class="mb-5" stage="Plan" current="1"/>

		<div class="mt-5 guide-container">

			<div class="row mx-1 py-3 justify-content-around">

				<div class="col-12 text-center">
					<h2>Create Your Meal Plan</h2>
				</div>

				<div class="col-12 mt-3">

					<transition mode="out-in" name="fade">

						<div class="mt-3 mb-3" v-if="loading">
							<loading-spinner/>
						</div>

						<form v-else>

							<div class="row" v-if="ready">
								<order-guide-plan class="col-12 col-lg-4"
														v-for="(meal_plan_info, index) in meal_plan_infos"
														:key="meal_plan_info.id" :index="index"
														:plans="plans(meal_plan_info.id)"
														:selected="selectedIndex === index"
														v-on:plan-selected="handlePlanSelection"
														:mealImage="mealImageArray[index]"
								/>
							</div>
						</form>

					</transition>

				</div>

				<div class="col-12 col-md-8 mt-3 px-5 text-center" v-if="!loading">
					<p class="font-weight-normal">
						Your initial order is for our standard menu. You will have the option to customize your menu after your
						initial order is placed if it is within our customization window.
					</p>
					<p class="font-weight-bold">
						<a target="_blank"
							href="https://support.healthyforlifemeals.com/hc/en-us/articles/360001410274-How-do-I-customize-my-order-">
							See details on customization deadlines.
						</a>
					</p>
				</div>

			</div>
		</div>


		<div class="spell-it-out col-12 mt-5 px-5 py-3" v-if="rec && basics">
			<h3 class="mb-3">*About Our Recommendation</h3>
			<p>We use the <a href="https://en.wikipedia.org/wiki/Harris%E2%80%93Benedict_equation" target="_blank">Harris
				Benedict Equation</a> to calculate your Basal Metabolic Rate.</p>

			<div v-if="basics.gender === 'Not Disclosed'">
				<p class="cals">The Equation we use does not have a non-binary option. We have made a recommendation based upon
					the Female gender, but also provide the male recommendation as well.</p>
			</div>

			<div>

				<p v-if="basics.gender === 'Female' || basics.gender === 'Not Disclosed'">
					BMR : (10 × <span class="cals">{{ (basics.weight * .453592).toFixed(1) }}</span> kg) + (6.25 × <span
					class="cals">{{ (basics.height * 2.54).toFixed(1) }}</span> cm) - (5 × <span class="cals">{{basics.age}} </span>
					years) - 161 + Active Calories = <span class="cals">{{women}}</span>
				</p>

				<p v-if="basics.gender === 'Male' || basics.gender === 'Not Disclosed'">
					BMR : (10 × <span class="cals">{{ (basics.weight * .453592).toFixed(1) }}</span> kg) + (6.25 × <span class="cals">{{ (basics.height * 2.54).toFixed(1) }}</span> cm) - (5 × <span
					class="cals">{{basics.age}} </span>
					years) + 5 + Active Calories = <span class="cals">{{men}}</span>
				</p>

			</div>

			<p>Based on the information you have provided, we feel our <span class="cals">{{possible_meal_plan ? possible_meal_plan : ''}}</span>
				calorie plan may be a good fit for you.</p>
			<p>This tool provides an estimate. If you have a metabolic disease such as diabetes, if you’re pregnant or
				breast-feeding or are a competitive athlete, the tool may over- or underestimate your actual calorie needs. We
				recommend you consult your doctor or dietitian before beginning any new meal plan.</p>
		</div>


		<div class="spell-it-out col-12 mt-5 px-5 py-3">
			<div class="d-flex flex-row">
				<icon fill="currentColor" name="heartbeat" class="icon"/>
				<label class="pl-1 pt-1"> denotes your last order.</label>
			</div>
			<hr class="mb-3"/>
			<div class="d-flex flex-row">
				<icon fill="currentColor" name="exclamation-circle" class="icon"/>
				<label class="pl-1">Allergen notification: All meals are produced in a facility that handles milk, egg, soy,
					wheat, fish, tree nut and peanut ingredients, and may contain these ingredients.</label>
			</div>
		</div>
		<div id="about-sub" class="spell-it-out col-12 mt-5 px-5 py-3">
			<h3>About Our Subscriptions</h3>
			<p>
				If you choose our subscription option, your order will automatically be renewed and your account charged each
				week until you choose to cancel. To stop a subscription, you must cancel prior to 7pm (CST/CDT) on Thursday for
				the
				following week’s meals.
			</p>
			<p>*For custom menu orders (you’ll have the option to customize your menu after your order
				is complete) the deadline to cancel a subscription is Monday at 7pm (CST/CDT) for the following week’s meals.
			</p>
			<h3>Fish Preference</h3>
			<p>
				Do you have a fish preference? We've got you covered! Simply select "No Fish" above, and our team will make
				sure all meals with fish are removed from your orders, and replaced with a "Chef's Choice" (you can also
				customize it after ordering if you like).
			</p>
		</div>
	</div>
</template>

<script>
	import OrderProgress from './OrderProgress';
	import LoadingSpinner from '../Globals/LoadingSpinner';
	import OrderGuidePlan from './OrderGuidePlan';
    // import LoadTwitterBootstrap from '../../mixins/LoadTwitterBootstrap'

	export default {
		name: 'order-plan',
        // mixins: [LoadTwitterBootstrap],
		components: {
			LoadingSpinner,
			OrderProgress,
			OrderGuidePlan
		},
		data() {
			return {
				rec: false,
				loading: true,
				selectedIndex: 0,
				possible_meal_plan: null,
				ready: false
			};
		},
		computed: {
			diabetes_or_blood_pressure() {
				return this.basics.conditions.includes('Diabetes') || this.basics.conditions.includes('High Blood Pressure');
			},
			meal_plan_infos() {
				return this.$store.getters.get_meal_plan_infos;
			},
			mealImageArray() {
				return ['https://static1.squarespace.com/static/59f0e6beace8641044d76e9c/5a1c3b6f652dea2e1a8f70e0/5a80b959652deaf340fdf06f/1518385914833/1200-D-Cheese-Lasagna-with-Vegetable-Marinara-and-Mixed-Vegetables.jpg', 'https://static1.squarespace.com/static/59f0e6beace8641044d76e9c/5a1fee3af9619aaae98e890d/5a80bccc8165f5c700acc550/1518386398773/1200-D-Blackened-Code-over-Quinoa-Black-Bean-Bake-and-Chunky-Vegetable-Salad.jpg', 'https://hflm-assets-prod.s3.us-east-2.amazonaws.com/meal_1028.jpeg'];
			},
			basics() {
				return JSON.parse(sessionStorage.getItem('basics'));
			},
			men() {
				var bmrCalc = parseInt((10 * (this.basics.weight * .453592)) + (6.25 * (this.basics.height * 2.54)) - (5 * this.basics.age) + 5);
				return this.total_intake(bmrCalc);
			},
			women() {
				return this.total_intake(parseInt((10 * this.basics.weight * .453592) + (6.25 * (this.basics.height * 2.54)) - (5 * this.basics.age) - 161));
			}
		},
		mounted() {
			if (this.$route.query.rec) {
				this.rec = true;
			}
			this.$eventHub.$on('meal-rec', plan => {
				this.possible_meal_plan = plan;
			});
			// this.$ga.ecommerce.send('pageview')
		},
		methods: {
			total_intake(num) {
				switch (this.basics.activityLevel) {
					case 'Low':
						return parseInt(num * 1.53);
					case 'Moderate':
						return parseInt(num * 1.76);
					case 'High':
						return parseInt(num * 2.25);
					default:
						return '';
				}
			},
			handlePlanSelection(payload) {
				this.selectedIndex = payload.index
			},
			plans(id) {
				return this.$store.state.meals.meal_plans.filter(plan => plan.mealPlanInfoId === id);
			}
		},
		beforeCreate() {
			this.loading = true;
			Promise.all([this.$store.dispatch('get_tax_rates'), this.$store.dispatch('get_meal_plans'), this.$store.dispatch('get_meal_plan_infos')])
			.then(() => {
				this.loading = false;
				this.ready = true;
			});
			this.$ga.ecommerce.addProduct({name: 'Meal Plans'});
			this.$ga.ecommerce.setAction('checkout', {'step': 1});
		}
	};
</script>

<style scoped lang="scss">
	@import '../../styles/scss/colors';

	.guide-container {
		background: white;
		border-radius: 5px;
		box-shadow: 2px 2px 2px 2px $silver;
	}

	.spell-it-out {
		border-radius: 5px;
		box-shadow: 2px 2px 2px 2px $silver;
	}

	.cals {
		color: $purple;
		font-weight: 600;
	}

	h3 {
		font-size: 18px;
	}

	.icon {
		color: $purple;
		position: relative;
		margin-right: 3px;
	}

	.highlight {
		box-shadow: 5px 5px 15px 15px $purple-background-2 !important;
		transition: box-shadow .5s ease-in;
	}

	#about-sub {
		background: white;
		border-radius: 5px;
		box-shadow: 2px 2px 2px 2px $silver;
	}
</style>
